import { useEffect, useRef } from 'react';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import {
  RUN_URL_PARAM,
  assertNeverRunUrlSyncAction,
  reconcileRunUrlSync,
} from './run-url-sync';

/**
 * Keeps the run open in the editor's artifact panel and the `?run=<id>` URL
 * param in step, in both directions — so a run is bookmarkable and survives a
 * reload, yet the breadcrumb and back arrow can still leave the run view.
 *
 * Not a {@link UrlParamWatchable}, which is the pattern where a model *owns* the
 * state and the URL is a projection of it (the editor tab, list params). The
 * open run is owned by the artifact panel's shared view stack — pushed and
 * popped by the back arrow, the breadcrumb, the close button, an artifact tab
 * switch, and any view stacked on top — so a watchable here would be a third
 * mirror rather than the source of truth.
 *
 * The refs remember what each side was last reconciled to, which is what lets
 * {@link reconcileRunUrlSync} tell which side moved instead of two effects
 * chasing each other. Reading a stale param the instant a run closes is exactly
 * what would reopen it and trap the user on the page, and adopting the URL
 * without writing back (`setFromSource`) would not prevent that — it drops the
 * one fact needed to tell a close from a deep link.
 */
export function useRunUrlSync(params: {
  /**
   * Only the top-level editor panel owns the URL. A nested sub-workflow view
   * shares this query string but has no run of its own, so letting it reconcile
   * would fight the outer panel.
   */
  enabled: boolean;
  searchParams: URLSearchParams;
  /** The run the panel is showing, or `undefined` for none. */
  openRunId: string | undefined;
  openRun: (runId: string) => void;
  /** Leave the run view — the URL dropped its run (e.g. browser back). */
  leaveRunView: () => void;
  urlAdapter: UrlParamAdapter;
}): void {
  const {
    enabled,
    searchParams,
    openRunId,
    openRun,
    leaveRunView,
    urlAdapter,
  } = params;

  const prevUrlRunRef = useRef(searchParams.get(RUN_URL_PARAM) ?? '');
  const prevOpenRunRef = useRef(openRunId ?? '');

  useEffect(() => {
    if (!enabled) {
      // Deliberately without touching the refs: a disabled pass reconciled
      // nothing, so it must not claim either side has been seen.
      return;
    }
    const urlRun = searchParams.get(RUN_URL_PARAM) ?? '';
    const runOpen = openRunId ?? '';
    const action = reconcileRunUrlSync({
      urlRun,
      openRun: runOpen,
      prevUrlRun: prevUrlRunRef.current,
      prevOpenRun: prevOpenRunRef.current,
    });
    prevUrlRunRef.current = urlRun;
    prevOpenRunRef.current = runOpen;

    switch (action.kind) {
      case 'none':
        return;
      case 'open-run':
        openRun(action.runId);
        return;
      case 'clear-panel':
        leaveRunView();
        return;
      case 'write-url':
        // The adapter deletes the key when the value is '', so writing '' scrubs
        // `?run=` from the URL, and a run id is encoded by the underlying
        // URLSearchParams (never hand-concatenated).
        urlAdapter.setParam(RUN_URL_PARAM, action.runId);
        return;
      default:
        return assertNeverRunUrlSyncAction(action);
    }
  }, [enabled, searchParams, openRunId, openRun, leaveRunView, urlAdapter]);
}
